import { GroupMemberRole, GroupMemberStatus } from '@/models/GroupMember'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'

import config from '@/config'
import { MemberStatus } from '@/models/Groups'
import Users from '@/models/Users'
import { useGlobalStore } from '@/store/app'
import { PaginationParams } from '@/types/PaginationParams'
import { isGokollab } from '@ghl/clientportal-core/init'
import BaseService from './BaseService'
import { ServiceWithStoreDependencies } from './decorators/serviceDecorators'
import { requests } from './HTTPService'
import {
  AddUserToGroupRequestType,
  SubmitMembershipAnswersRequestType,
} from './types/UsersService.type'

const http = requests(config?.COMMUNITIES_SERVER_URL)

@ServiceWithStoreDependencies('groupId')
class UsersServiceCls extends BaseService<Users> {
  constructor() {
    super(null as any)
  }

  model() {
    return Users
  }

  get endpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}/users`
  }
  get baseEndpoint(): string {
    const appStore = useGlobalStore()
    return `/${appStore.locationId}/groups/${appStore.groupId}`
  }

  async getGroupMembersList(
    memberRole?: GroupMemberRole[],
    memberStatus?: GroupMemberStatus
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/list`, {
      params: {
        memberRole: memberRole?.toString(),
        memberStatus,
      },
    })
    return response.data
  }

  async getGroupMemberRequestCount(): Promise<any> {
    const response = await http.get(`${this.endpoint}/request/count`)
    return response.data
  }

  async getGroupMembersDetails(
    memberRole?: GroupMemberRole,
    memberStatus?: GroupMemberStatus,
    searchText?: string,
    PaginationParams?: PaginationParams,
    showEmail?: boolean
  ): Promise<any> {
    const response = await http.get(`${this.endpoint}/details`, {
      params: {
        memberRole,
        memberStatus,
        searchText,
        ...PaginationParams,
        showEmail,
      },
    })
    return response.data
  }

  async getGroupMembersDetailsForChannelSuggestions(
    channelId: string,
    searchText?: string,
    memberRole?: GroupMemberRole,
    PaginationParams?: PaginationParams
  ): Promise<any> {
    const response = await http.get(
      `${this.endpoint}/channel-suggestions/${channelId}`,
      {
        params: {
          memberRole,
          searchText,
          ...PaginationParams,
        },
      }
    )
    return response.data
  }

  async getUserDataBySlug(slug: string): Promise<any> {
    return http
      .get(`${this.endpoint}`, {
        params: { slug: slug, isGokollabUser: isGokollab() },
      })
      .then(res => res.data)
  }

  async getUserDataBycontactId(contactId: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/${contactId}`)
    return response.data
  }

  async joinGroup(
    role: string,
    contactId: string,
    status: MemberStatus,
    orderId: string
  ): Promise<any> {
    const response = await http.post(`${this.endpoint}`, {
      contactId,
      role,
      status,
      orderId,
    })
    trackCustomGAEvents(GAEventTypes.GROUP_JOINED, {
      locationId: this.locationId,
    })
    return response.data
  }

  async submitMembershipAnswer(
    payload: SubmitMembershipAnswersRequestType
  ): Promise<any> {
    const response = await http.post(
      `${this.endpoint}/joining-questionnaire`,
      payload
    )
    return response.data
  }

  async updateUserGroup(payload: AddUserToGroupRequestType): Promise<any> {
    const { contactId } = payload
    const url = `${this.endpoint}/${contactId}`
    const response = await http.patch(url, payload)
    return response.data
  }

  async removeUser(contactId: string): Promise<any> {
    const response = await http.delete(`${this.endpoint}/${contactId}`)
    return response.data
  }

  async getUserDetailsForCurrentGroup(): Promise<any> {
    const response = await http.get(`${this.baseEndpoint}/user-group-details`)
    return response.data
  }
  async getUserGroups(contactId: string): Promise<any> {
    const response = await http.get(`${this.endpoint}/${contactId}`)
    return response.data
  }
}

// Export singleton instance
export const UsersService = new UsersServiceCls()

// Export type for dependency injection
export type IUsersService = UsersServiceCls

// Export commonly used methods directly
export const {
  getGroupMembersList,
  getGroupMemberRequestCount,
  getGroupMembersDetails,
  getGroupMembersDetailsForChannelSuggestions,
  getUserDataBySlug,
  getUserDataBycontactId,
  joinGroup,
  submitMembershipAnswer,
  updateUserGroup,
  removeUser,
  getUserGroups,
} = UsersService
